
import type { NeighborhoodDetail } from '../../../../mocks/neighborhoodDetails';

interface NeighborhoodMapProps {
  neighborhood: NeighborhoodDetail;
}

export default function NeighborhoodMap({ neighborhood }: NeighborhoodMapProps) {
  const mapSrc = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12461.5!2d-121.288!3d38.752!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809b2a1c4e5b0c5d%3A0x0!2s${neighborhood.mapQuery}!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus`;

  return (
    <section id="map" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Explore the Area
            </h2>
            <p className="text-gray-500 text-sm">
              Find businesses and attractions in {neighborhood.name}
            </p>
          </div>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${neighborhood.mapQuery}`}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-navigation-line"></i>
            Open in Google Maps
          </a>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-lg border border-gray-100">
            <div className="w-full h-[450px] relative">
              <iframe
                src={mapSrc}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`${neighborhood.name} Map - Roseville CA`}
                className="absolute inset-0"
              ></iframe>
            </div>
          </div>

          {/* Business Quick List */}
          <div className="bg-gray-50 rounded-2xl border border-gray-100 p-5 overflow-hidden">
            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-2-fill text-teal-600"></i>
              </div>
              Top Locations
            </h3>
            <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
              {neighborhood.businesses.slice(0, 8).map((biz, idx) => (
                <a
                  key={biz.id}
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(biz.name + ' ' + biz.address)}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 hover:border-teal-200 hover:shadow-sm transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={biz.image}
                      alt={biz.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-teal-600 transition-colors truncate">
                      {idx + 1}. {biz.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="flex items-center gap-0.5">
                        <i className="ri-star-fill text-amber-400 text-[10px]"></i>
                        <span className="text-xs font-semibold text-gray-700">{biz.rating}</span>
                      </div>
                      <span className="text-[10px] text-gray-400">({biz.reviews})</span>
                      <span className="text-[10px] text-gray-400">&middot;</span>
                      <span className="text-[10px] text-gray-500 truncate">{biz.type}</span>
                    </div>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-teal-500 transition-colors"></i>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
